import { type GitChangeRequestView, type GitHostKind } from '@apm/contracts';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNarrowScreen } from '../../../lib/use-narrow-screen';
import { cn } from '../../../lib/utils';
import {
  CHANGE_REQUEST_STRIPE_CLASS,
  changeRequestBadge,
  changeRequestRef,
  displayMomentDate,
  gitAge,
} from '../git-format';
import { GIT_CHANGE_REQUEST_ROW_SLOTS } from '../slots';
import {
  GitChangeRequestBadge,
  GitCiDot,
  GitReviewMark,
  GitTargetChips,
  useGitAgeText,
} from './git-marks';

/**
 * Cụm dấu của **một MR**: trạng thái review, chấm CI, rồi tới các chỗ cắm
 * (`GIT_CHANGE_REQUEST_ROW_SLOTS`). Dùng ở bảng Merge request, thẻ di động và
 * khối "Phát triển" — task sau thêm một dấu thì cả ba nơi có nó.
 *
 * Nhận `asOf` để chuyển cho component cắm vào (luật #7); cụm này không tự đọc gì.
 */
export function GitChangeRequestMarks({
  projectId,
  changeRequest,
  asOf,
}: {
  readonly projectId: string;
  readonly changeRequest: GitChangeRequestView;
  readonly asOf: string | undefined;
}): ReactNode {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" data-testid="git-cr-marks">
      <GitReviewMark state={changeRequest.reviewState} />
      <GitCiDot status={changeRequest.ciStatus} url={changeRequest.ciUrl} />
      {GIT_CHANGE_REQUEST_ROW_SLOTS.map((Slot, index) => (
        <Slot key={index} projectId={projectId} changeRequest={changeRequest} asOf={asOf} />
      ))}
    </span>
  );
}

/**
 * Cột "Tuổi": MR đang mở ⇒ đã mở bao lâu (tính tới `asOf`, hoặc tới bây giờ);
 * MR đã xong ⇒ ngày merge / đóng. GitHub để `closedAt` trống cho PR đã merge,
 * GitLab để trống với MR chưa từng đóng — mốc nào vắng thì lùi về mốc cập nhật.
 */
function useChangeRequestAge(asOf: string | undefined, now: Date) {
  const { t } = useTranslation();
  const ageText = useGitAgeText();
  return (changeRequest: GitChangeRequestView): string => {
    if (changeRequest.state === 'merged') {
      return t('gitTab.changeRequests.mergedOn', {
        date: displayMomentDate(changeRequest.mergedAt ?? changeRequest.updatedAtExt),
      });
    }
    if (changeRequest.state === 'closed') {
      return t('gitTab.changeRequests.closedOn', {
        date: displayMomentDate(changeRequest.closedAt ?? changeRequest.updatedAtExt),
      });
    }
    return ageText(gitAge(changeRequest.createdAtExt, asOf ?? now));
  };
}

/**
 * Danh sách MR / PR — APM-GIT-e, docs/plan-git-integration.md §8.1: tiêu đề, chip
 * mã việc, tác giả, nhánh → nhánh, trạng thái review, chấm CI, tuổi.
 *
 * Màn rộng là **bảng** (so hàng với hàng); điện thoại là **thẻ tách khối** có
 * vạch màu theo trạng thái và ô chỉ số đều cỡ — không bày cả hai rồi ẩn bằng CSS
 * (hai bản của cùng một MR trong DOM là hai thứ để trình đọc màn hình đọc).
 *
 * ⚠️ Không tự đọc dữ liệu: `items` do màn gọi lấy với `asOf` của nó. `asOf` ở đây
 * chỉ để tính "tuổi" tới đúng mốc đang xem và chuyển cho chỗ cắm (luật #7).
 */
export function GitChangeRequestList({
  projectId,
  items,
  asOf,
  hostKindOf,
  showRepository,
  selectedKey,
  onOpen,
  now = new Date(),
}: {
  readonly projectId: string;
  readonly items: readonly GitChangeRequestView[];
  readonly asOf: string | undefined;
  /** Loại máy chủ của một kho — quyết định viết `!45` hay `#45`. */
  readonly hostKindOf: (repositoryId: string) => GitHostKind | undefined;
  /** Dự án nối nhiều kho ⇒ mỗi hàng nói mình thuộc kho nào. */
  readonly showRepository: boolean;
  /** `changeRequestKey` của MR đang mở ngăn bên. */
  readonly selectedKey?: string | null | undefined;
  readonly onOpen: (changeRequest: GitChangeRequestView) => void;
  readonly now?: Date;
}): ReactNode {
  const { t } = useTranslation();
  const narrow = useNarrowScreen();
  const age = useChangeRequestAge(asOf, now);
  const refOf = (item: GitChangeRequestView): string =>
    changeRequestRef(hostKindOf(item.repositoryId), item.number);
  const isSelected = (item: GitChangeRequestView): boolean =>
    selectedKey === `${item.repositoryId}:${item.number}`;
  const noTask = t('gitTab.changeRequests.noTask');

  if (narrow) {
    return (
      <ul className="flex flex-col gap-2" data-testid="git-cr-cards">
        {items.map((item) => (
          <li
            key={item.id}
            data-testid="git-cr"
            className={cn(
              'flex flex-col gap-2 overflow-hidden rounded-xl border border-l-4 border-border bg-card p-3',
              CHANGE_REQUEST_STRIPE_CLASS[changeRequestBadge(item)],
            )}
          >
            <button
              type="button"
              onClick={() => onOpen(item)}
              className="flex w-full min-w-0 flex-col gap-1 text-left"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="font-mono text-xs text-muted-foreground">{refOf(item)}</span>
                <GitChangeRequestBadge changeRequest={item} />
              </span>
              <span className="break-words text-sm font-semibold">{item.title}</span>
            </button>
            <dl className="grid grid-cols-2 gap-1.5 text-xs">
              <Cell label={t('gitTab.changeRequests.column.author')}>
                {item.authorName ?? item.authorUsername ?? '—'}
              </Cell>
              <Cell label={t('gitTab.changeRequests.column.age')}>{age(item)}</Cell>
            </dl>
            <p className="break-all font-mono text-[11px] text-muted-foreground">
              {showRepository ? `${item.repositoryPath} · ` : ''}
              {item.sourceBranch} → {item.targetBranch}
            </p>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <GitTargetChips targets={item.targets} emptyLabel={noTask} />
              <GitChangeRequestMarks projectId={projectId} changeRequest={item} asOf={asOf} />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="overflow-x-auto rounded-DEFAULT border border-border">
      <table className="w-full text-sm" data-testid="git-cr-table">
        <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">{t('gitTab.changeRequests.column.title')}</th>
            <th className="px-3 py-2 font-medium">{t('gitTab.changeRequests.column.tasks')}</th>
            <th className="px-3 py-2 font-medium">{t('gitTab.changeRequests.column.author')}</th>
            <th className="px-3 py-2 font-medium">{t('gitTab.changeRequests.column.branches')}</th>
            <th className="px-3 py-2 font-medium">{t('gitTab.changeRequests.column.review')}</th>
            <th className="px-3 py-2 font-medium">{t('gitTab.changeRequests.column.age')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              data-testid="git-cr"
              data-selected={isSelected(item) || undefined}
              onClick={() => onOpen(item)}
              className={cn(
                'cursor-pointer border-t border-border align-top hover:bg-muted/40',
                isSelected(item) && 'bg-muted/60',
              )}
            >
              <td className="max-w-md px-3 py-2">
                {/* Nút thật trong ô đầu: hàng bấm được bằng chuột, còn bàn phím vào qua đây. */}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpen(item);
                  }}
                  className="flex min-w-0 flex-col gap-0.5 text-left"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{refOf(item)}</span>
                    <GitChangeRequestBadge changeRequest={item} />
                  </span>
                  <span className="break-words font-medium">{item.title}</span>
                  {showRepository ? (
                    <span className="break-all text-[11px] text-muted-foreground">
                      {item.repositoryPath}
                    </span>
                  ) : null}
                </button>
              </td>
              <td className="px-3 py-2">
                <GitTargetChips targets={item.targets} emptyLabel={noTask} />
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-xs">
                {item.authorName ?? item.authorUsername ?? '—'}
              </td>
              <td className="px-3 py-2 font-mono text-[11px]">
                <span className="break-all">{item.sourceBranch}</span>
                <span className="text-muted-foreground"> → </span>
                <span className="break-all">{item.targetBranch}</span>
              </td>
              <td className="px-3 py-2">
                <GitChangeRequestMarks projectId={projectId} changeRequest={item} asOf={asOf} />
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums">{age(item)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cell({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-border bg-muted/40 px-2 py-1.5">
      <dt className="truncate text-[11px] leading-[13px] text-muted-foreground">{label}</dt>
      <dd className="font-semibold leading-tight">{children}</dd>
    </div>
  );
}
